// Precio de modificadores por ZONA del plato (pizza mitad y mitad, cuartos).
//
// FUENTE DE VERDAD COMPARTIDA por web/POS/kiosko y por el servidor que cotiza.
// Diseño: docs/superpowers/specs/2026-07-29-pizza-zoned-modifiers-design.md
//
// DESVIACIÓN DELIBERADA: este módulo opera en CENTAVOS ENTEROS, a diferencia de
// lib/utils/pricing.ts que opera en float decimal. Motivo (§2.3 del spec): un
// ingrediente de $1.15 prorrateado a la mitad da 0.57 en float y 0.58 en enteros.
// Half-up sobre float pierde un centavo por unidad, sistemáticamente, en el camino
// de dinero. No generalizar el cambio al resto de pricing.ts en este PR.

import { effectiveUnitPrice, type PricingConfig } from "@/lib/utils/pricing"

export type Zone = "whole" | "left" | "right" | "q1" | "q2" | "q3" | "q4"
export type ZoneLayout = "whole" | "halves" | "quarters"
export type ZonedPricingMode = "prorated" | "full"

/** Zonas propias de cada disposición. `whole` es válida en TODAS y por eso no
 *  aparece aquí: ver zoneBelongsToLayout. */
export const ZONES_BY_LAYOUT: Record<ZoneLayout, readonly Zone[]> = {
  whole: ["whole"],
  halves: ["left", "right"],
  quarters: ["q1", "q2", "q3", "q4"],
}

/** Fracción como PAR DE ENTEROS [numerador, denominador]. Nunca 0.5 ni 0.25 en
 *  float: multiplicar centavos por un float reintroduce el error de $1.15. */
const ZONE_RATIO: Record<Zone, readonly [number, number]> = {
  whole: [1, 1],
  left: [1, 2],
  right: [1, 2],
  q1: [1, 4],
  q2: [1, 4],
  q3: [1, 4],
  q4: [1, 4],
}

/** GEOMETRÍA: qué porción del plato ocupa la zona. Es el valor que se graba en
 *  order_item_modifiers.zone_fraction (CHECK: 1|0.5|0.25). NO es la fracción de
 *  precio aplicada: en pricing_mode='full' se cobra entero sobre fracción 0.5. */
export function zoneFraction(zone: Zone): number {
  const [num, den] = ZONE_RATIO[zone]
  return num / den
}

/** Espejo exacto del CHECK `oim_zone_in_layout` de Postgres. Si divergen, la orden
 *  se rechaza en la base: mantener los dos lados iguales. */
export function zoneBelongsToLayout(zone: Zone, layout: ZoneLayout): boolean {
  if (zone === "whole") return true
  return (ZONES_BY_LAYOUT[layout] as readonly Zone[]).includes(zone)
}

/** Half-away-from-zero. `Math.round` solo sirve para positivos: Math.round(-57.5)
 *  da -57 (hacia +infinito), lo que haría que un descuento y su equivalente
 *  positivo redondeen distinto. */
export function roundHalfAwayFromZero(n: number): number {
  return n < 0 ? -Math.round(-n) : Math.round(n)
}

/**
 * Decimal → centavos enteros. PUNTO DE CUANTIZACIÓN CANÓNICO del módulo.
 *
 * Hace dos cosas a la vez, y las dos son deliberadas:
 *  1. Corrige la representación float (1.15*100 = 114.99999999999999 → 115).
 *  2. Cuantiza sub-centavos. modifier_options.price_delta es `numeric` SIN escala,
 *     así que un 0.586 importado por script llega intacto y aquí pasa a 59. Es un
 *     redondeo documentado, no un segundo redondeo invisible.
 */
export function toCents(amount: number): number {
  return roundHalfAwayFromZero(amount * 100)
}

/**
 * Precio POR UNIDAD, EN CENTAVOS ENTEROS, de un modificador en una zona.
 * Esta es la función canónica: quien multiplique por cantidad debe usar ESTA, no la
 * de dólares (0.58 × 3 = 1.7399999999999998).
 *
 * `baseCardPrice` es el valor CRUDO de la rejilla precio×tamaño
 * (modifier_option_size_prices.price_delta) o, si no hay fila, el
 * modifier_options.price_delta del fallback de D10. Quien lo lee de la base es el
 * llamador (PR 3); esta función es pura a propósito.
 */
export function resolveZonedUnitPriceCents(args: {
  baseCardPrice: number
  zone: Zone
  mode: ZonedPricingMode
  cfg: PricingConfig
  /** D7: los incluidos del ítem valen 0 en cualquier zona. El servidor lo deriva de
   *  modifier_options.is_default, NUNCA del payload del cliente. */
  isIncluded?: boolean
}): number {
  // D7 primero: un incluido no necesita fila de rejilla, así que exigirle un precio
  // finito lo haría fallar por algo que no le aplica.
  if (args.isIncluded) return 0

  // numeric en Postgres ACEPTA NaN como valor válido. Un undefined (fila de rejilla
  // ausente) o un string de supabase-js se vuelven NaN, se insertan sin error, y de
  // ahí todo SUM(price_delta) de la orden, del turno y del reporte es NaN.
  if (typeof args.baseCardPrice !== "number" || !Number.isFinite(args.baseCardPrice)) {
    throw new Error(
      `zonedPricing: baseCardPrice no finito (${String(args.baseCardPrice)}) para zona ${args.zone}`
    )
  }

  const cents = toCents(effectiveUnitPrice(args.baseCardPrice, args.cfg))
  if (args.mode === "full") return cents
  const [num, den] = ZONE_RATIO[args.zone]
  return roundHalfAwayFromZero((cents * num) / den)
}

/** Envoltorio en dólares para los consumidores actuales de price_delta. NO usar
 *  para multiplicar por cantidad: ver resolveZonedUnitPriceCents. */
export function resolveZonedUnitPrice(
  args: Parameters<typeof resolveZonedUnitPriceCents>[0]
): number {
  return resolveZonedUnitPriceCents(args) / 100
}

/** Una selección de modificador con su zona. Identidad de una selección en memoria;
 *  NO es un espejo completo de la fila de order_item_modifiers (PR 3 le agrega
 *  option_name, modifier_group_id y size_option_id al construir el insert). */
export type ZonedSelection = {
  optionId: string
  zone: Zone
  quantity: number
  isRemoval: boolean
  serveOnSide: boolean
  /** D7/D12: la unidad que el ítem ya trae incluida. Vale 0 y vive en su propia
   *  fila, separada de las unidades extra cobradas. */
  isIncluded: boolean
}

/** Slot: identidad física de una selección. Dos filas con el mismo slot son la
 *  misma cosa y sus cantidades se suman. */
function slotKey(s: ZonedSelection): string {
  return [
    s.optionId,
    s.zone,
    s.isRemoval ? 1 : 0,
    s.serveOnSide ? 1 : 0,
    s.isIncluded ? 1 : 0,
  ].join("|")
}

/**
 * PASO 0 OBLIGATORIO antes de cotizar o colapsar: suma las cantidades por slot.
 *
 * El reductor agrega implícitamente al incrementar cantidad en addOption, pero el
 * SERVIDOR no puede depender del reductor: el body del request es entrada controlada
 * por el cliente. Un payload con la misma zona repetida haría que el colapso vea
 * cobertura total y cobre una unidad donde se pidieron varias.
 */
export function aggregateZonedSelections(selections: ZonedSelection[]): ZonedSelection[] {
  const bySlot = new Map<string, ZonedSelection>()
  for (const s of selections) {
    const k = slotKey(s)
    const prev = bySlot.get(k)
    if (prev) prev.quantity += s.quantity
    else bySlot.set(k, { ...s })
  }
  return [...bySlot.values()]
}

/** Llave de colapso: DEBE incluir cantidad, removal, aparte e incluido. Colapsar
 *  solo por optionId cobraría ×1 cuando el cliente pidió ×1 izquierda + ×2 derecha. */
function collapseKey(s: ZonedSelection): string {
  return [
    s.optionId,
    s.quantity,
    s.isRemoval ? 1 : 0,
    s.serveOnSide ? 1 : 0,
    s.isIncluded ? 1 : 0,
  ].join("|")
}

/** Llave de fusión de enteras: la cantidad NO entra, porque al fusionar se suma. */
function wholeKey(s: ZonedSelection): string {
  return [s.optionId, s.isRemoval ? 1 : 0, s.serveOnSide ? 1 : 0, s.isIncluded ? 1 : 0].join("|")
}

/**
 * Si una opción cubre TODAS las zonas de la disposición con cantidad, removal,
 * aparte e incluido idénticos, la colapsa a una sola selección `whole`.
 *
 * Sin esto, $2.99 en los 4 cuartos cobra round(0.7475) × 4 = $3.00 — un centavo más
 * que la pizza entera, imposible de explicarle al cliente.
 *
 * Agrega internamente (defensa en profundidad: el servidor no puede olvidarse) y es
 * idempotente. Orden de salida estable por llave para que los tests y la comparación
 * de líneas de carrito no dependan del orden de entrada.
 *
 * ORDEN: agregar → VALIDAR → colapsar. Validar después del colapso reportaría
 * "4 whole" en vez de señalar la zona culpable. Ver §7 del spec.
 */
export function collapseFullCoverage(
  selections: ZonedSelection[],
  layout: ZoneLayout
): ZonedSelection[] {
  const layoutZones = ZONES_BY_LAYOUT[layout]
  const wholes: ZonedSelection[] = []
  const zoned: ZonedSelection[] = []
  for (const s of aggregateZonedSelections(selections)) {
    if (s.zone === "whole") wholes.push(s)
    else zoned.push(s)
  }

  const byKey = new Map<string, ZonedSelection[]>()
  for (const s of zoned) {
    const k = collapseKey(s)
    const list = byKey.get(k) ?? []
    list.push(s)
    byKey.set(k, list)
  }

  const kept: ZonedSelection[] = []
  for (const group of byKey.values()) {
    const covered = new Set(group.map((s) => s.zone))
    const isFull = layoutZones.every((z) => covered.has(z))
    if (isFull) wholes.push({ ...group[0], zone: "whole" })
    else kept.push(...group)
  }

  // Fusiona enteras equivalentes sumando cantidad: 1 entera + (1 izq + 1 der
  // colapsadas) son 2 enteras, no dos filas de 1.
  const merged = new Map<string, ZonedSelection>()
  for (const s of wholes) {
    const k = wholeKey(s)
    const prev = merged.get(k)
    if (prev) prev.quantity += s.quantity
    else merged.set(k, { ...s })
  }

  return [...kept, ...merged.values()].sort((a, b) =>
    `${a.optionId}|${a.zone}`.localeCompare(`${b.optionId}|${b.zone}`)
  )
}
